'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { AuthorityRole } from '@mentora/shared-components'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { AUTHORITY_ROLES } from '@mentora/shared-components'
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, Switch } from '@mentora/ui-components'
import { Building2, CheckCircle2, Eye, Power, PowerOff, UserPlus, XCircle } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import Link from 'next/link'
import { useMemo, useState, useTransition } from 'react'

interface OrganizationItem {
  id: string
  name: string
  slug: string
  logo: string | null
  isActive: boolean
  createdAt: string
}

export interface OrganizationsPanelActions {
  createOrganization: (input: {
    name: string
    logo?: string
  }) => Promise<ActionResult>
  inviteOrgMemberAsAppAdmin: (input: {
    organizationId: string
    email: string
    role: AuthorityRole
    isInstructor?: boolean
  }) => Promise<ActionResult>
  setOrgActiveStatus: (input: {
    organizationId: string
    isActive: boolean
  }) => Promise<ActionResult>
}

export interface OrganizationsPanelProps {
  organizations: OrganizationItem[]
  /** The storefront's own server actions — the one thing the two copies of this screen never shared. */
  actions: OrganizationsPanelActions
}

interface OrgFormState {
  name: string
  logo: string
}

const emptyOrgForm: OrgFormState = {
  name: '',
  logo: '',
}

export function OrganizationsPanel({ organizations, actions }: OrganizationsPanelProps) {
  const format = useFormatter()
  const t = useTranslations('organizations')
  const tRoles = useTranslations('organizations.roles')
  const tCommon = useTranslations('common')
  const { showError, t: tt } = useActionToast()
  const [isPending, startTransition] = useTransition()
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success', text: string } | null>(null)
  const [search, setSearch] = useState('')

  const [createOpen, setCreateOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)

  const [form, setForm] = useState<OrgFormState>(emptyOrgForm)
  const [inviteOrgId, setInviteOrgId] = useState<string>('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<AuthorityRole>('admin')
  const [inviteIsInstructor, setInviteIsInstructor] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<OrganizationItem | null>(null)

  const filteredOrganizations = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term)
      return organizations
    return organizations.filter(org =>
      org.name.toLowerCase().includes(term) || org.slug.toLowerCase().includes(term),
    )
  }, [organizations, search])

  function resetFeedback() {
    setFeedback(null)
  }

  function openCreate() {
    resetFeedback()
    setForm(emptyOrgForm)
    setCreateOpen(true)
  }

  function openInvite(orgId?: string) {
    resetFeedback()
    setInviteOrgId(orgId ?? organizations[0]?.id ?? '')
    setInviteEmail('')
    setInviteRole('admin')
    setInviteIsInstructor(false)
    setInviteOpen(true)
  }

  function toggleActiveStatus(org: OrganizationItem, nextActive: boolean) {
    runAction(
      () => actions.setOrgActiveStatus({ organizationId: org.id, isActive: nextActive }),
      tt(
        nextActive
          ? 'organizations.activation.toasts.activated'
          : 'organizations.activation.toasts.deactivated',
        { name: org.name },
      ),
      () => setDeactivateTarget(null),
    )
  }

  function runAction(
    action: () => Promise<ActionResult>,
    successMessage: string,
    onDone?: () => void,
  ) {
    startTransition(async () => {
      resetFeedback()
      const result = await action()
      if (!result.ok) {
        showError(result)
        setFeedback({ kind: 'error', text: tt(result.errorKey, result.errorValues) })
        return
      }
      setFeedback({ kind: 'success', text: successMessage })
      onDone?.()
    })
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-8">
        <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-background to-muted/30 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="font-(--font-display) text-3xl tracking-tight text-foreground">{t('title')}</h1>
              <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => openInvite()}>
                <UserPlus className="size-4" />
                {t('inviteOwnerAdmin')}
              </Button>
              <Button onClick={openCreate}>
                <Building2 className="size-4" />
                {t('createOrganization')}
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="max-w-sm"
            />
          </div>
          {feedback && (
            <p className={`mt-4 text-sm ${feedback.kind === 'error' ? 'text-destructive' : 'text-success'}`}>
              {feedback.text}
            </p>
          )}
        </div>

        <div className="grid gap-4">
          {filteredOrganizations.map(org => (
            <Card key={org.id} className="border-border/50">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-xl">{org.name}</CardTitle>
                      {org.isActive
                        ? (
                            <Badge variant="success" className="gap-1">
                              <CheckCircle2 className="size-3" />
                              {t('activation.status.active')}
                            </Badge>
                          )
                        : (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="size-3" />
                              {t('activation.status.inactive')}
                            </Badge>
                          )}
                    </div>
                    <CardDescription>
                      /
                      {org.slug}
                      {' · '}
                      {t('createdOn', { date: format.dateTime(new Date(org.createdAt), { dateStyle: 'medium' }) })}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link href={`/organizations/${org.id}`}>
                        <Eye className="size-4" />
                        {t('view')}
                      </Link>
                    </Button>
                    {org.isActive
                      ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isPending}
                            onClick={() => setDeactivateTarget(org)}
                            className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          >
                            <PowerOff className="size-4" />
                            {t('activation.actions.deactivate')}
                          </Button>
                        )
                      : (
                          <Button
                            size="sm"
                            variant="default"
                            disabled={isPending}
                            onClick={() => toggleActiveStatus(org, true)}
                          >
                            <Power className="size-4" />
                            {t('activation.actions.activate')}
                          </Button>
                        )}
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
          {filteredOrganizations.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {t('noOrganizationsFound')}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('createSheet.title')}</SheetTitle>
            <SheetDescription>{t('createSheet.description')}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('fields.name')}</label>
              <Input
                placeholder={t('fields.namePlaceholder')}
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                {t('fields.logoUrl')}
                {' '}
                <span className="text-muted-foreground font-normal">{t('fields.logoOptional')}</span>
              </label>
              <Input
                placeholder={t('fields.logoPlaceholder')}
                value={form.logo}
                onChange={e => setForm(prev => ({ ...prev, logo: e.target.value }))}
              />
            </div>
          </div>
          <SheetFooter>
            <Button
              disabled={isPending}
              onClick={() =>
                runAction(
                  () => actions.createOrganization(form),
                  t('toasts.created'),
                  () => setCreateOpen(false),
                )}
            >
              {isPending ? tCommon('saving') : tCommon('create')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('inviteSheet.title')}</SheetTitle>
            <SheetDescription>{t('inviteSheet.description')}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4">
            <Select value={inviteOrgId} onValueChange={setInviteOrgId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    {`${org.name} (${org.slug})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder={t('fields.emailPlaceholder')}
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
            />
            <Select
              value={inviteRole}
              onValueChange={value => setInviteRole(value as AuthorityRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTHORITY_ROLES.map(option => (
                  <SelectItem key={option} value={option}>
                    {tRoles(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center justify-between gap-2 rounded-md border border-border/60 p-3">
              <span className="text-sm font-medium text-foreground">
                {tRoles('instructor')}
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  {tt('organization.fields.isInstructorHint')}
                </span>
              </span>
              <Switch
                checked={inviteIsInstructor}
                onCheckedChange={setInviteIsInstructor}
              />
            </label>
          </div>
          <SheetFooter>
            <Button
              disabled={isPending || !inviteOrgId}
              onClick={() =>
                runAction(
                  () =>
                    actions.inviteOrgMemberAsAppAdmin({
                      organizationId: inviteOrgId,
                      email: inviteEmail,
                      role: inviteRole,
                      isInstructor: inviteIsInstructor,
                    }),
                  t('toasts.invitationSent'),
                  () => setInviteOpen(false),
                )}
            >
              {isPending ? t('buttons.sending') : t('buttons.send')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog
        open={deactivateTarget !== null}
        onOpenChange={open => !open && setDeactivateTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('activation.confirm.title')}</DialogTitle>
            <DialogDescription>
              {deactivateTarget && t('activation.confirm.body', { name: deactivateTarget.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeactivateTarget(null)}
              disabled={isPending}
            >
              {t('activation.confirm.cancel')}
            </Button>
            <Button
              variant="default"
              disabled={isPending}
              onClick={() => deactivateTarget && toggleActiveStatus(deactivateTarget, false)}
              className="bg-destructive text-overlay-foreground hover:bg-destructive/90"
            >
              {t('activation.confirm.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
